import { css } from "@linaria/core";
import cx from "classnames";
import React, { memo, useCallback, useDeferredValue, useEffect, useMemo } from "react";
import t from "../../../../../../../../i18n";
import Button from "@ui/Button";
import { RefreshLine } from "@ui/IconV2/react";
import TooltipV2 from "@ui/TooltipV2";
import type { BlockPlotModelT } from "../../../../../schema/block/plot";
import { printableHiddenInline } from "../../../../../utils/print";
import { usePlotPeriod } from "./hook/usePlotPeriod";
import { usePlotLink } from "./hook/usePlotLink";
import * as io from "io-ts";
import createGlobalState from "@ui/lib/createGlobalState";
import {
  convertPresetToPeriod,
  getDateRangeNavigationPresets,
} from "../../../../../component/navigation";
import type { Project } from "../../../../../../../../api/useProjects";
import Highcharts, { type YAxisOptions } from "highcharts";
import type { Options } from "@ui/Highcharts";
import { usePreset } from "./hook/usePreset";
import { setAxisExtremesFromSeriesZoom } from "./utils/zoom";

const useResetZoomLocal = createGlobalState({
  key: "dashboardResetZoomLocal",
  storage: "session",
  type: io.boolean,
  defaultValue: false,
  scoped: true,
});

const useResetZoomGlobal = createGlobalState({
  key: "dashboardResetZoomGlobal",
  storage: "session",
  type: io.boolean,
  defaultValue: false,
  scoped: false,
});

const ResetZoomButtonHidden = css`
  opacity: 0;
  pointer-events: none;
`;

const AnimatableResetButton = css`
  transition: opacity 150ms ease-in-out 200ms;

  ${printableHiddenInline}
`;

const TooltipTextOverlay = css`
  max-width: 320px;
`;

export const ResetZoomButton = memo(
  ({
    lastCycle,
    block,
    maxPeriod,
    project,
    plot,
  }: {
    lastCycle?: { from: number; to: number };
    block: BlockPlotModelT;
    maxPeriod: { from: number; to: number };
    project: Project;
    plot: React.RefObject<Highcharts.Chart>;
  }) => {
    const { isLinked } = usePlotLink(block);
    const [selectedPeriod, setSelectedPeriod] = usePlotPeriod(block);
    const [canResetZoomLocally, setResetZoomLocal] = useResetZoomLocal({ scope: block.id });
    const [canResetZoomGlobally, setResetZoomGlobal] = useResetZoomGlobal({});
    const preset = usePreset(block);

    const presets = useMemo(
      () =>
        getDateRangeNavigationPresets({
          from: maxPeriod.from,
          to: maxPeriod.to,
          cycle: lastCycle,
          timezone: project.timezone,
        }),
      [maxPeriod, lastCycle, project],
    );

    const initialTemporalZoom = useMemo(() => {
      const preset = presets.find(
        preset => preset.metadata?.["id"] === block.config.options?.temporalZoom,
      );
      return preset
        ? convertPresetToPeriod(preset, project.timezone)
        : { from: maxPeriod.from, to: maxPeriod.to };
    }, [
      block.config.options?.temporalZoom,
      maxPeriod.from,
      maxPeriod.to,
      presets,
      project.timezone,
    ]);

    const canResetZoom = useMemo(
      () => (isLinked ? canResetZoomGlobally : canResetZoomLocally),
      [isLinked, canResetZoomGlobally, canResetZoomLocally],
    );

    // Reset to block zoom min / max if defined
    const resetZoom = useCallback(() => {
      if (!plot.current) return;

      setResetZoomLocal(false);
      setResetZoomGlobal(false);
      setSelectedPeriod(initialTemporalZoom);
      setAxisExtremesFromSeriesZoom(plot.current.yAxis, "y");

      if (preset === "line") {
        setAxisExtremesFromSeriesZoom(plot.current.xAxis, "x", initialTemporalZoom);
      }

      if (preset === "scatter") {
        setAxisExtremesFromSeriesZoom(plot.current.xAxis, "x");
      }
    }, [
      initialTemporalZoom,
      plot,
      preset,
      setResetZoomGlobal,
      setResetZoomLocal,
      setSelectedPeriod,
    ]);

    const deferedSelectedPeriod = useDeferredValue(selectedPeriod);

    useEffect(() => {
      let currentValue;
      if (typeof deferedSelectedPeriod === "string") {
        const preset =
          presets.find(preset => preset.metadata?.["id"] === deferedSelectedPeriod) ?? null;
        currentValue = preset ? convertPresetToPeriod(preset, project.timezone) : null;
        if (!currentValue) return;
      } else if (deferedSelectedPeriod) {
        currentValue = deferedSelectedPeriod;
      }

      /**
       * @note On init, we can't be sure that plot.current exists
       * so we can't compare plot zoom & current selected period.
       */
      if (!currentValue || !plot.current) {
        setResetZoomLocal(s => s || false);
        setResetZoomGlobal(s => s || false);
        return;
      }

      const canResetZoom =
        currentValue.from !== initialTemporalZoom.from ||
        currentValue.to !== initialTemporalZoom.to;

      setResetZoomLocal(canResetZoom);
      setResetZoomGlobal(canResetZoom);
    }, [
      initialTemporalZoom,
      presets,
      project.timezone,
      deferedSelectedPeriod,
      setResetZoomGlobal,
      setResetZoomLocal,
      plot,
    ]);

    return (
      <TooltipV2
        overlay={<div className={TooltipTextOverlay}>{t("tooltip.block.plot.zoom.reset")}</div>}
      >
        <Button
          variant="tertiary"
          leftIcon={<RefreshLine />}
          onClick={resetZoom}
          className={cx(AnimatableResetButton, {
            [ResetZoomButtonHidden]: !canResetZoom,
          })}
        >
          {t("project.dashboard.block.zoom.reset.title")}
        </Button>
      </TooltipV2>
    );
  },
);

export function useWithResetZoomOptions(
  block: BlockPlotModelT,
  options: Options | null,
): Options | null {
  const [, setResetZoomLocal] = useResetZoomLocal({ scope: block.id });
  const [, setResetZoomGlobal] = useResetZoomGlobal({});
  const preset = usePreset(block);

  const afterSetExtremesHandler = useCallback(
    (e: Highcharts.AxisSetExtremesEventObject) => {
      if (e.trigger !== "DEFAULT" && e.trigger !== undefined) {
        setResetZoomLocal(true);
        setResetZoomGlobal(true);
      }
    },
    [setResetZoomGlobal, setResetZoomLocal],
  );

  return useMemo(
    () =>
      options
        ? {
            ...options,
            ...(preset === "scatter"
              ? {
                  xAxis: (options.xAxis as Highcharts.XAxisOptions[]).map(axis => ({
                    ...axis,
                    events: {
                      afterSetExtremes: afterSetExtremesHandler,
                    },
                  })),
                }
              : {}),
            yAxis: (options.yAxis as YAxisOptions[]).map(axis => ({
              ...axis,
              events: {
                afterSetExtremes: afterSetExtremesHandler,
              },
            })),
          }
        : null,
    [afterSetExtremesHandler, options, preset],
  );
}
